// BUNDLE MANAGER — Admin CMS for the Recommendation Engine V1.
// Create / Edit / Pause / Delete bundles. Each bundle = curated multi-medication
// package surfaced post-assessment by /api/recommendations/suggest.
// Owner picks medications from the catalog (no slug typing) and sets bundle price.
import { useCallback, useEffect, useState } from "react";
import {
  Package, Plus, Trash2, Save, Eye, EyeOff, X, GripVertical, ChevronUp, ChevronDown,
} from "lucide-react";
import { API, RED, NEON, authHeader } from "./shared";

const CATEGORIES = [
  { slug: "weight-loss",   label: "Weight Loss" },
  { slug: "mens-health",   label: "Men's Health" },
  { slug: "womens-health", label: "Women's Health" },
  { slug: "combo",         label: "Cross-Category Combo" },
];

const blank = () => ({
  slug: "", name: "", category: "weight-loss", description: "",
  medication_slugs: [], bundle_price_cents: 0, savings_cents: 0,
  benefits: [], image_url: "", active: true, order: 999,
});

export function BundleManager({ token }) {
  const [bundles, setBundles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [b, c] = await Promise.all([
      fetch(`${API}/recommendations/bundles?include_inactive=true`).then((r) => r.json()),
      fetch(`${API}/catalog/medications?include_inactive=true`).then((r) => r.json()),
    ]);
    setBundles(b || []); setCatalog(c || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(b) {
    const url = isNew
      ? `${API}/recommendations/bundles`
      : `${API}/recommendations/bundles/${b.slug}`;
    const method = isNew ? "POST" : "PATCH";
    const body = isNew ? b : Object.fromEntries(
      Object.entries(b).filter(([k]) => !["id", "created_at", "updated_at", "slug", "medications"].includes(k))
    );
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Save failed" }));
      alert(`Save failed: ${err.detail || res.status}`);
      return;
    }
    await load();
    setEditing(null); setIsNew(false);
  }

  async function togglePause(b) {
    await fetch(`${API}/recommendations/bundles/${b.slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ active: !b.active }),
    });
    load();
  }

  async function remove(slug) {
    if (!confirm(`Delete bundle "${slug}"? This cannot be undone.`)) return;
    await fetch(`${API}/recommendations/bundles/${slug}`, {
      method: "DELETE", headers: authHeader(token),
    });
    load();
  }

  async function seed() {
    if (!confirm("Seed 3 starter bundles (Weight Loss, Men's Performance, Hair Recovery)?")) return;
    await fetch(`${API}/recommendations/demo/seed`, { method: "POST" });
    load();
  }

  return (
    <div className="space-y-4" data-testid="bundle-manager">
      <div className="rounded-sm border border-white/10 bg-black/50">
        <header className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2">
            <Package size={14} color={RED} /> RECOMMENDATION BUNDLES · {bundles.length}
          </p>
          <div className="flex items-center gap-2">
            {bundles.length === 0 && (
              <button onClick={seed}
                data-testid="bundle-seed-btn"
                className="px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase border border-white/20 text-white/70 hover:text-white">
                Seed Starters
              </button>
            )}
            <button onClick={() => { setEditing(blank()); setIsNew(true); }}
              data-testid="bundle-add-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
              style={{ background: RED, borderColor: RED, color: "white" }}>
              <Plus size={12} /> New Bundle
            </button>
          </div>
        </header>

        <div className="divide-y divide-white/5 max-h-[640px] overflow-y-auto">
          {loading && <p className="text-center text-white/45 text-[12px] py-10">Loading…</p>}
          {!loading && bundles.length === 0 && (
            <p className="text-center text-white/45 text-[12px] py-10" data-testid="bundle-empty">
              No bundles yet. Click "Seed Starters" or "New Bundle".
            </p>
          )}
          {bundles.map((b) => (
            <div key={b.slug} className="grid grid-cols-12 gap-2 px-5 py-3 items-center hover:bg-white/[0.02]"
              data-testid={`bundle-row-${b.slug}`}>
              <button onClick={() => { setEditing({ ...b }); setIsNew(false); }}
                data-testid={`bundle-edit-${b.slug}`}
                className="col-span-12 sm:col-span-5 text-left">
                <p className="font-heavy text-[13px] text-white">{b.name}</p>
                <p className="text-[10.5px] font-mono text-white/50">{b.slug}</p>
              </button>
              <div className="col-span-6 sm:col-span-3">
                <span className="text-[10px] tracking-[0.14em] font-extrabold uppercase text-white/55">
                  {(b.medications || []).map((m) => m.name).join(" + ") || "—"}
                </span>
              </div>
              <div className="col-span-3 sm:col-span-2 text-right">
                <p className="font-heavy text-[13px] text-white">${Math.round((b.bundle_price_cents || 0) / 100)}</p>
                {b.savings_cents > 0 && (
                  <p className="text-[9.5px] font-extrabold tracking-[0.12em] uppercase" style={{ color: NEON }}>
                    Save ${Math.round(b.savings_cents / 100)}
                  </p>
                )}
              </div>
              <div className="col-span-3 sm:col-span-2 flex justify-end gap-1">
                <button onClick={() => togglePause(b)}
                  data-testid={`bundle-pause-${b.slug}`}
                  className="p-1.5">
                  {b.active
                    ? <Eye size={13} color={NEON} />
                    : <EyeOff size={13} color="rgba(255,255,255,0.4)" />}
                </button>
                <button onClick={() => remove(b.slug)}
                  data-testid={`bundle-del-${b.slug}`}
                  className="p-1.5 text-white/45 hover:text-[#FF2A3C]">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <BundleEditor
          bundle={editing}
          isNew={isNew}
          catalog={catalog}
          onChange={(patch) => setEditing((e) => ({ ...e, ...patch }))}
          onSave={() => save(editing)}
          onClose={() => { setEditing(null); setIsNew(false); }}
        />
      )}
    </div>
  );
}

/* ─────────────  EDITOR  ───────────── */
function BundleEditor({ bundle, isNew, catalog, onChange, onSave, onClose }) {
  const set = (k) => (v) => onChange({ [k]: v });
  const slugs = bundle.medication_slugs || [];

  const toggleMed = (slug) => {
    const next = slugs.includes(slug)
      ? slugs.filter((s) => s !== slug)
      : [...slugs, slug];
    onChange({ medication_slugs: next });
  };

  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= slugs.length) return;
    const arr = [...slugs];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange({ medication_slugs: arr });
  };

  // Helper: compute the savings hint based on selected meds
  const summedIndividual = slugs
    .map((s) => (catalog.find((m) => m.slug === s)?.starting_price_cents || 0))
    .reduce((a, b) => a + b, 0);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="bundle-editor">
      <button aria-label="Close" onClick={onClose} className="flex-1 bg-black/60 backdrop-blur-sm" />
      <aside className="w-full max-w-[640px] bg-[#0A0B10] border-l border-white/10 flex flex-col">
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold text-white/55">
              {isNew ? "NEW BUNDLE" : "EDIT BUNDLE"}
            </p>
            <p className="font-heavy text-white text-[18px] tracking-tight">{bundle.name || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase border border-white/20 text-white/70 hover:text-white">
              Cancel
            </button>
            <button onClick={onSave}
              data-testid="bundle-editor-save"
              className="inline-flex items-center gap-1 px-4 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase"
              style={{ background: RED, color: "white" }}>
              <Save size={12} /> Save
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {/* Basics */}
          <Section title="Basics">
            <Field label="Name *">
              <input value={bundle.name} onChange={(e) => set("name")(e.target.value)}
                data-testid="bundle-field-name" className={inputCls()} />
            </Field>
            <Field label={isNew ? "Slug *" : "Slug (read-only)"}>
              <input value={bundle.slug} disabled={!isNew}
                onChange={(e) => set("slug")(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                data-testid="bundle-field-slug"
                className={inputCls() + (isNew ? "" : " opacity-60")} />
            </Field>
            <Field label="Category">
              <select value={bundle.category} onChange={(e) => set("category")(e.target.value)}
                data-testid="bundle-field-category" className={inputCls()}>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <textarea value={bundle.description}
                onChange={(e) => set("description")(e.target.value)}
                rows={3}
                data-testid="bundle-field-desc"
                className={inputCls()} />
            </Field>
            <Field label="">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bundle.active}
                  onChange={(e) => set("active")(e.target.checked)}
                  data-testid="bundle-field-active"
                  className="accent-[#FF2A3C] w-4 h-4" />
                <span className="text-[12px] font-extrabold tracking-[0.10em] uppercase text-white">
                  Active (show in recommendations)
                </span>
              </label>
            </Field>
          </Section>

          {/* Medications picker */}
          <Section title={`Medications In Bundle — ${slugs.length} selected`}>
            <p className="text-[10.5px] text-white/45 -mt-1">
              Click to add or remove. Use ↑ ↓ next to selected items to set display order.
            </p>
            {/* Selected (ordered) */}
            {slugs.length > 0 && (
              <div className="space-y-1.5">
                {slugs.map((s, idx) => {
                  const m = catalog.find((x) => x.slug === s);
                  return (
                    <div key={s} className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-[#FF2A3C]/40 bg-[#FF2A3C]/10"
                      data-testid={`bundle-selected-${s}`}>
                      <GripVertical size={12} className="text-white/35" />
                      <span className="flex-1 text-[12px] font-extrabold text-white tracking-[0.06em]">
                        {(m?.name || s).toUpperCase()}
                      </span>
                      <span className="text-[10px] font-mono text-white/55">
                        ${Math.round((m?.starting_price_cents || 0) / 100)}/mo
                      </span>
                      <button onClick={() => move(idx, -1)} disabled={idx === 0}
                        className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronUp size={12} /></button>
                      <button onClick={() => move(idx, +1)} disabled={idx === slugs.length - 1}
                        className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronDown size={12} /></button>
                      <button onClick={() => toggleMed(s)}
                        className="text-white/45 hover:text-[#FF2A3C]"><X size={12} /></button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* All medications, grouped by category */}
            <div className="mt-3 max-h-72 overflow-y-auto rounded-sm border border-white/10 divide-y divide-white/5">
              {catalog
                .filter((m) => !slugs.includes(m.slug))
                .map((m) => (
                  <button key={m.slug} type="button"
                    onClick={() => toggleMed(m.slug)}
                    data-testid={`bundle-pick-${m.slug}`}
                    className="w-full px-2.5 py-1.5 text-left flex items-center gap-2 hover:bg-white/[0.04]">
                    <Plus size={11} className="text-white/45" />
                    <span className="flex-1 text-[12px] text-white tracking-[0.04em]">{m.name}</span>
                    <span className="text-[9.5px] tracking-[0.14em] font-extrabold uppercase text-white/45">{m.category}</span>
                    <span className="text-[10px] font-mono text-white/55">
                      ${Math.round((m.starting_price_cents || 0) / 100)}/mo
                    </span>
                  </button>
                ))}
            </div>
          </Section>

          {/* Pricing */}
          <Section title="Pricing">
            <p className="text-[10.5px] text-white/45 -mt-1">
              Individually these meds would cost <span className="text-white font-extrabold">${Math.round(summedIndividual / 100)}/mo</span> total.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <PriceField label="Bundle Monthly Price"
                cents={bundle.bundle_price_cents}
                onChange={(c) => set("bundle_price_cents")(c)}
                testid="bundle-price-monthly" />
              <PriceField label="Savings Shown To Patient"
                cents={bundle.savings_cents}
                onChange={(c) => set("savings_cents")(c)}
                testid="bundle-price-savings" />
            </div>
            <button type="button"
              onClick={() => set("savings_cents")(Math.max(0, summedIndividual - (bundle.bundle_price_cents || 0)))}
              data-testid="bundle-autosave"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-extrabold tracking-[0.14em] uppercase border border-white/20 text-white/70 hover:text-white">
              Auto-Calc Savings
            </button>
          </Section>

          {/* Benefits */}
          <Section title="Benefits (shown on bundle card)">
            <ListEditor
              items={bundle.benefits || []}
              onChange={(arr) => set("benefits")(arr)}
              testid="bundle-benefits" />
          </Section>
        </div>
      </aside>
    </div>
  );
}

/* shared editor helpers */
function inputCls() {
  return "w-full bg-black border border-white/15 px-2.5 py-1.5 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none";
}
function Section({ title, children }) {
  return (
    <section className="rounded-sm border border-white/10 bg-black/40 p-4 space-y-3">
      <p className="text-[10.5px] tracking-[0.18em] font-extrabold uppercase text-white/55">{title}</p>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <div>
      {label && <p className="text-[10px] tracking-[0.14em] font-bold text-white/45 mb-1 uppercase">{label}</p>}
      {children}
    </div>
  );
}
function PriceField({ label, cents, onChange, testid }) {
  const [str, setStr] = useState(cents == null ? "" : (cents / 100).toString());
  useEffect(() => { setStr(cents == null ? "" : (cents / 100).toString()); }, [cents]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-1">
        <span className="text-white/55 font-mono text-[12px]">$</span>
        <input value={str}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9.]/g, "");
            setStr(v);
            onChange(v === "" ? 0 : Math.round(parseFloat(v) * 100));
          }}
          data-testid={testid}
          className={inputCls()} />
      </div>
    </Field>
  );
}
function ListEditor({ items, onChange, testid }) {
  const set = (i, v) => { const arr = [...items]; arr[i] = v; onChange(arr); };
  const add = () => onChange([...(items || []), ""]);
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((v, i) => (
        <div key={i} className="flex gap-1.5">
          <input value={v} onChange={(e) => set(i, e.target.value)}
            placeholder="e.g. Accelerated Fat Loss"
            className={inputCls()} />
          <button onClick={() => del(i)} className="text-white/45 hover:text-[#FF2A3C]">
            <X size={13} />
          </button>
        </div>
      ))}
      <button onClick={add}
        className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase border border-dashed border-white/20 text-white/70 hover:text-white">
        <Plus size={11} /> Add Benefit
      </button>
    </div>
  );
}
